import { z } from 'zod';
import { PaginationQuerySchema } from './common.js';

/**
 * Admin-side management schemas: admin users, roles, menus, nodes.
 */

export const AdminLoginSchema = z.object({
  username: z.string().min(3).max(32),
  password: z.string().min(6).max(128),
  /** Mandatory in prod; optional in dev via env flag. */
  captcha: z.string().optional(),
});
export type AdminLoginRequest = z.infer<typeof AdminLoginSchema>;

export const CreateAdminUserSchema = z.object({
  username: z
    .string()
    .min(3)
    .max(32)
    .regex(/^[a-zA-Z0-9_.]+$/),
  password: z.string().min(8).max(128),
  nickname: z.string().max(32).optional(),
  email: z.string().email().max(128).optional(),
  role_ids: z.array(z.number().int().positive()).min(1, 'at least one role required'),
});
export type CreateAdminUserRequest = z.infer<typeof CreateAdminUserSchema>;

export const UpdateAdminUserSchema = z.object({
  nickname: z.string().max(32).optional(),
  email: z.string().email().max(128).optional().nullable(),
  status: z.enum(['active', 'locked']).optional(),
  role_ids: z.array(z.number().int().positive()).optional(),
  /** Optional password reset — only super role may include this. */
  new_password: z.string().min(8).max(128).optional(),
});
export type UpdateAdminUserRequest = z.infer<typeof UpdateAdminUserSchema>;

export const AdminUserListQuerySchema = PaginationQuerySchema.extend({
  keyword: z.string().max(64).optional(),
  status: z.enum(['active', 'locked']).optional(),
  role_id: z.coerce.number().int().positive().optional(),
});

export const CreateRoleSchema = z.object({
  name: z.string().min(2).max(32),
  description: z.string().max(200).optional(),
  menu_ids: z.array(z.number().int().positive()).default([]),
  node_ids: z.array(z.number().int().positive()).default([]),
});
export type CreateRoleRequest = z.infer<typeof CreateRoleSchema>;

export const UpdateRoleSchema = CreateRoleSchema.partial();
export type UpdateRoleRequest = z.infer<typeof UpdateRoleSchema>;
